import { Pressable, StyleSheet } from 'react-native'

import { Box, Text, useTheme } from '../theme/restyle'

export interface FiatKycOptionRowProps {
  title: string
  detail: string
  /** Words on the chip; the design says "Go" for every route so far. */
  action: string
  /** Where the detail breaks; the frames set it per route. */
  detailWidth?: number
  onPress: () => void
}

/**
 * One route out of a held deposit, inside the identity options card (Figma
 * KYC R2, 2387:13638).
 */
export function FiatKycOptionRow({
  title,
  detail,
  action,
  detailWidth = DEFAULT_DETAIL_WIDTH,
  onPress,
}: FiatKycOptionRowProps) {
  const theme = useTheme()

  return (
    <Pressable
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={`${title}. ${detail}`}
      style={[styles.row, { backgroundColor: theme.background.pill.default }]}
    >
      <Box flex={1} style={styles.copy}>
        <Text
          weight="medium"
          style={[styles.title, { color: theme.content.elements.primary }]}
        >
          {title}
        </Text>
        <Text
          style={[
            styles.detail,
            { maxWidth: detailWidth, color: theme.content.elements.secondary },
          ]}
        >
          {detail}
        </Text>
      </Box>
      <Box
        style={[
          styles.chip,
          // The same ghosted wash the back disc wears, so the chip reads
          // quieter than the row it sits on.
          { backgroundColor: theme.content.elements.ghosted },
        ]}
      >
        <Text
          weight="medium"
          style={[
            styles.action,
            { color: theme.content.button.secondary.default },
          ]}
        >
          {action}
        </Text>
      </Box>
    </Pressable>
  )
}

/** The widest the frames draw it; shorter routes pass their own. */
const DEFAULT_DETAIL_WIDTH = 235

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    // The frames draw 90; a row grows past it rather than clipping its copy
    // at a larger text size or in a longer language.
    minHeight: 90,
    paddingLeft: 22,
    paddingRight: 16,
    borderRadius: 26,
  },
  copy: { gap: 4 },
  title: { fontSize: 17, lineHeight: 19, letterSpacing: -0.34 },
  detail: {
    fontSize: 11.5,
    lineHeight: 14,
    letterSpacing: -0.23,
  },
  chip: {
    width: 60,
    height: 38,
    borderRadius: 32,
    alignItems: 'center',
    justifyContent: 'center',
  },
  action: { fontSize: 15, lineHeight: 18, letterSpacing: -0.3 },
})
